/**
 * Bild-Ausschnitt festlegen (Migration 723, Mockup freigegeben 14.10.2026): der Betreiber tippt die
 * wichtigste Stelle eines Fotos an; die Angebotsseite legt sie per CSS object-position fest, damit
 * sie bei jeder Bildschirmbreite sichtbar bleibt. Gespeichert als „x% y%“, Mitte = null.
 */
import { useState, type MouseEvent } from "react";
import { Button, Icon, Modal } from "./components";

export type Punkt = { x: number; y: number };

export function punktLesen(wert: string | null | undefined): Punkt {
  const m = /^([\d.]+)% ([\d.]+)%$/.exec(wert ?? "");
  return m ? { x: Number(m[1]), y: Number(m[2]) } : { x: 50, y: 50 };
}

/** Mitte = null, damit die Seite ihre eigene Voreinstellung behält. */
export function punktWert(p: Punkt): string | null {
  return p.x === 50 && p.y === 50 ? null : `${p.x}% ${p.y}%`;
}

/** Für die öffentliche Seite: ohne gespeicherten Punkt bleibt die CSS-Klasse wirksam. */
export function fokusStil(wert: string | null | undefined) {
  return wert ? { objectPosition: wert } : undefined;
}

export function BildAusschnittDialog({
  open,
  onClose,
  label,
  url,
  wert,
  istHero,
  onSpeichern,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  url: string;
  wert: string | null;
  istHero: boolean;
  onSpeichern: (wert: string | null) => Promise<void>;
}) {
  const [punkt, setPunkt] = useState<Punkt>(() => punktLesen(wert));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const position = `${punkt.x}% ${punkt.y}%`;

  function antippen(e: MouseEvent<HTMLDivElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const prozent = (v: number) => Math.min(100, Math.max(0, Math.round(v * 100)));
    setPunkt({ x: prozent((e.clientX - r.left) / r.width), y: prozent((e.clientY - r.top) / r.height) });
  }

  async function speichern() {
    setBusy(true);
    setFehler(null);
    try {
      await onSpeichern(punktWert(punkt));
      onClose();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const vorschauBild = (className: string) => (
    <img src={url} alt="" className={`h-full w-full object-cover ${className}`} style={{ objectPosition: position }} data-testid="ausschnitt-vorschau" />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="860px"
      title="Bild-Ausschnitt festlegen"
      subtitle={label}
      testId="bild-ausschnitt-dialog"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void speichern()} disabled={busy} data-testid="ausschnitt-speichern">
            {busy ? "Speichert …" : "Ausschnitt speichern"}
          </Button>
        </>
      }
    >
      <div className="grid gap-6 md:grid-cols-[3fr_2fr]">
        <div>
          <p className="mb-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Tippe auf die wichtigste Stelle des Fotos.
          </p>
          <div className="relative cursor-crosshair overflow-hidden rounded-lg" onClick={antippen} data-testid="ausschnitt-flaeche">
            <img src={url} alt="" className="block w-full select-none" draggable={false} />
            <span
              className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary/70 shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
              style={{ left: `${punkt.x}%`, top: `${punkt.y}%` }}
              data-testid="ausschnitt-punkt"
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface" data-testid="ausschnitt-text">
              Punkt: {punkt.x} % von links, {punkt.y} % von oben
            </span>
            <Button variant="ghost" type="button" onClick={() => setPunkt({ x: 50, y: 50 })}>
              Auf Mitte zurücksetzen
            </Button>
          </div>
          <p className="mt-2 flex items-start gap-1.5 rounded-md bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-body-sm text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
            <Icon name="info" className="mt-0.5 text-[15px]" />
            <span>Der Punkt bleibt bei jeder Bildschirmbreite sichtbar. Beim Ersetzen des Fotos springt er auf die Mitte zurück.</span>
          </p>
          {fehler && <p className="mt-2 font-body-sm text-[11px] text-error">{fehler}</p>}
        </div>
        <div className="space-y-4">
          <span className="block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Vorschau</span>
          {istHero ? (
            <>
              <div>
                <div className="aspect-[12/5] overflow-hidden rounded-md border border-outline-variant/50">{vorschauBild("")}</div>
                <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">Computer</span>
              </div>
              <div>
                <div className="h-48 w-28 overflow-hidden rounded-md border border-outline-variant/50">{vorschauBild("")}</div>
                <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">Handy</span>
              </div>
            </>
          ) : (
            <div className="w-56 overflow-hidden rounded-lg border border-outline-variant/50 bg-surface dark:bg-inverse-surface">
              <div className="h-[100px]">{vorschauBild("")}</div>
              <div className="space-y-1.5 p-3">
                <div className="h-2.5 w-3/4 rounded bg-surface-container-high dark:bg-white/10" />
                <div className="h-2 w-1/2 rounded bg-surface-container-high dark:bg-white/10" />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
